import { createContext, useCallback, useContext, useEffect, useState } from 'react';

/** Where the console remembers the rail's state, per browser. */
export const RAIL_KEY = 'concord.rail';
/** Below this width the rail starts collapsed unless the user chose otherwise. */
export const NARROW_QUERY = '(max-width: 900px)';

/** The stored choice wins; with none, narrow screens start collapsed. */
export function initialCollapsed(stored: string | null, narrow: boolean): boolean {
  if (stored === 'collapsed') return true;
  if (stored === 'expanded') return false;
  return narrow;
}

function read(): boolean {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(RAIL_KEY);
  } catch {
    // Storage can be blocked; fall back to the width default.
  }
  const narrow = typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
  return initialCollapsed(stored, narrow);
}

export interface RailState {
  collapsed: boolean;
  toggle: () => void;
}

/** Collapsed or expanded, remembered across pages and visits. */
export function useRailState(): RailState {
  const [collapsed, setCollapsed] = useState(read);
  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch {
      // Not remembered this time; the toggle still works.
    }
  }, [collapsed]);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return { collapsed, toggle };
}

export const RailContext = createContext<RailState | null>(null);

/** The shell's rail state, or a local one when the rail is rendered on its own. */
export function useRail(): RailState {
  const shared = useContext(RailContext);
  const local = useRailState();
  return shared ?? local;
}
